<template>
  <div class="platform-refund-panel">
    <div class="pr-header">
      <div class="pr-title">
        <i class="leftLine"></i>
        <span>{{ $t('平台退款单') }}</span>
      </div>
      <div v-if="canPlatformRefund" class="pr-actions">
        <a-button style="margin-right: 8px" :loading="syncing" @click="syncRefund">
          <a-icon type="sync" />
          {{ $t('同步平台退款单') }}
        </a-button>
        <a-button type="primary" @click="openApplyModal">
          {{ $t('申请平台退款') }}
        </a-button>
      </div>
    </div>

    <a-table
      :columns="columns"
      :dataSource="list"
      :loading="loading"
      :pagination="false"
      bordered
      size="middle"
      rowKey="platformRefundOrderId"
      :scroll="{ x: 2000 }"
    />

    <div class="pr-footer">
      <pagination
        :pageNo="pageNo"
        :length="pageSize"
        :total-count="total"
        @paginate="onPaginate"
      ></pagination>
    </div>

    <a-modal
      v-model="applyVisible"
      :title="$t('申请平台退款')"
      :width="900"
      :footer="null"
      @cancel="onApplyCancel"
    >
      <a-spin :spinning="deviceLoading">
        <div class="pr-section-title">{{ $t('选择退款设备') }}</div>
        <div v-if="!deviceLoading && resourceList.length === 0" class="pr-empty">
          {{ $t('暂无可退款设备') }}
        </div>
        <a-radio-group
          v-else
          v-model="selectedDeviceId"
          class="pr-device-list"
          @change="onDeviceChange"
        >
          <a-radio
            v-for="item in resourceList"
            :key="item.resourceNo"
            :value="item.resourceNo"
            class="pr-device-item"
          >
            {{ item.resourceLabel || item.resourceNo }}（{{ item.resourceNo }}）
          </a-radio>
        </a-radio-group>
      </a-spin>

      <a-spin :spinning="amountLoading">
        <template v-if="selectedDeviceId">
          <div class="pr-section-title mt4 mb4">
            {{ $t('预计申请退款总额') }}：
            <span class="pr-amount">{{ requestedAmount | toThousands }}</span>
          </div>
          <a-table
            :columns="feeColumns"
            :dataSource="feeList"
            :pagination="false"
            bordered
            size="small"
            :rowKey="(record, index) => index"
            :scroll="{ x: 1100 }"
          />
        </template>
      </a-spin>

      <div class="pr-modal-footer mt10">
        <a-button style="margin-right: 8px" @click="onApplyCancel">
          {{ $t('form.Cancel') }}
        </a-button>
        <a-button
          type="primary"
          :loading="submitting"
          :disabled="!selectedDeviceId || Number(requestedAmount) <= 0"
          @click="onSubmit"
        >
          {{ $t('提交') }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import http from '@/service/axios'
import api from '@/service/api'
import pagination from '@component/pagination'
import { toThousands } from '@/common/filters'

export default {
  name: 'PlatformRefundPanel',
  components: {
    pagination,
  },
  props: {
    supplierReturnOrderId: {
      type: [String, Number],
      default: '',
    },
    // 为 true 时展示“申请平台退款”入口
    canPlatformRefund: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      list: [],
      pageNo: 1,
      pageSize: 10,
      total: 0,
      // 申请退款弹窗
      applyVisible: false,
      syncing: false,
      deviceLoading: false,
      amountLoading: false,
      submitting: false,
      resourceList: [],
      selectedDeviceId: '',
      requestedAmount: 0,
      feeList: [],
      statusList: ['SUBMITTING', 'UNKNOWN', 'WAIT_PLATFORM_RESULT', 'SUCCESS', 'CLOSED', 'FAILED'],
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('平台退款单号'),
          dataIndex: 'platformRefundOrderSn',
          width: 190,
        },
        {
          title: this.$t('采购退货单号'),
          dataIndex: 'supplierReturnOrderSn',
          width: 180,
        },
        {
          title: this.$t('平台'),
          dataIndex: 'platform',
          width: 100,
        },
        {
          title: this.$t('店铺'),
          dataIndex: 'shopCode',
          width: 120,
        },
        {
          title: this.$t('平台售后单号'),
          dataIndex: 'platformAftersaleSn',
          width: 180,
        },
        {
          title: this.$t('平台退款单编号'),
          dataIndex: 'platformRefundSn',
          width: 180,
        },
        {
          title: this.$t('设备'),
          dataIndex: 'deviceOrderId',
          width: 220,
          customRender: (text, record) => {
            if (record.deviceLabel && record.deviceOrderId) {
              return `${record.deviceLabel}（${record.deviceOrderId}）`
            }
            return record.deviceLabel || record.deviceOrderId || '-'
          },
        },
        {
          title: this.$t('退款状态'),
          dataIndex: 'status',
          width: 120,
          customRender: (text) => this.formatStatus(text),
        },
        {
          title: this.$t('平台状态'),
          dataIndex: 'platformStatusLabel',
          width: 120,
          customRender: (text, record) =>
            record.platformStatusLabel || record.platformStatus || '-',
        },
        {
          title: this.$t('申请金额'),
          dataIndex: 'requestedAmount',
          width: 120,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('已退款金额'),
          dataIndex: 'refundedAmount',
          width: 120,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('创建时间'),
          dataIndex: 'createdAt',
          width: 180,
        },
        {
          title: this.$t('提交时间'),
          dataIndex: 'submittedAt',
          width: 180,
        },
        {
          title: this.$t('最近同步时间'),
          dataIndex: 'lastSyncedAt',
          width: 180,
        },
        {
          title: this.$t('完成时间'),
          dataIndex: 'completedAt',
          width: 180,
        },
      ]
    },
    feeColumns() {
      return [
        {
          title: this.$t('账单编号'),
          dataIndex: 'billNo',
          width: 180,
        },
        {
          title: this.$t('费用名称'),
          dataIndex: 'feeTypeLabel',
          width: 140,
          customRender: (text, record) => record.feeTypeLabel || record.feeTypeCode || '-',
        },
        {
          title: this.$t('账单金额'),
          dataIndex: 'billAmount',
          width: 120,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('已支付金额'),
          dataIndex: 'paidAmount',
          width: 120,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('历史已退款金额'),
          dataIndex: 'refundedAmount',
          width: 130,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('可退款金额'),
          dataIndex: 'refundableAmount',
          width: 120,
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: this.$t('本次预计申请金额'),
          dataIndex: 'requestedRefundAmount',
          width: 150,
          customRender: (text) => this.formatAmount(text),
        },
      ]
    },
  },
  watch: {
    supplierReturnOrderId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.pageNo = 1
          this.fetchList()
        }
      },
    },
  },
  methods: {
    formatAmount(value) {
      if (value === undefined || value === null || value === '') return '-'
      return toThousands(value)
    },
    formatStatus(status) {
      if (!status) return '-'
      return this.statusList.includes(status) ? this.$t('form.' + status) : status
    },
    onPaginate(data) {
      this.pageNo = data.pageNo
      this.pageSize = data.pageSize
      this.fetchList()
    },
    async fetchList() {
      if (!this.supplierReturnOrderId) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const res = await http({
          url: api.platformRefundList,
          type: 'post',
          data: {
            supplierReturnOrderId: this.supplierReturnOrderId,
            pageNo: this.pageNo,
            pageSize: this.pageSize,
          },
        })
        const result = (res && res.result) || {}
        this.list = result.platformRefundOrders || []
        this.total = result.total || 0
      } finally {
        this.loading = false
      }
    },
    async syncRefund() {
      if (!this.supplierReturnOrderId) return
      this.syncing = true
      try {
        await http({
          url: api.platformRefundSyncList,
          type: 'post',
          hasLoading: true,
          data: {
            supplierReturnOrderId: this.supplierReturnOrderId,
          },
        })
        this.$success({
          title: this.$t('layer.Success'),
          content: this.$t('同步成功'),
        })
        this.pageNo = 1
        this.fetchList()
      } finally {
        this.syncing = false
      }
    },
    openApplyModal() {
      this.selectedDeviceId = ''
      this.requestedAmount = 0
      this.feeList = []
      this.resourceList = []
      this.applyVisible = true
      this.fetchRefundableDevices()
    },
    onApplyCancel() {
      this.applyVisible = false
    },
    async fetchRefundableDevices() {
      if (!this.supplierReturnOrderId) return
      this.deviceLoading = true
      try {
        const res = await http({
          url: api.platformRefundRefundableInfo,
          type: 'post',
          data: {
            supplierReturnOrderId: this.supplierReturnOrderId,
          },
        })
        this.resourceList = (res && res.result && res.result.resourceList) || []
        // 只有一条可退款设备时默认选中
        if (this.resourceList.length === 1) {
          this.selectedDeviceId = this.resourceList[0].resourceNo
          this.fetchRefundableAmount()
        }
      } finally {
        this.deviceLoading = false
      }
    },
    onDeviceChange(e) {
      this.selectedDeviceId = e.target.value
      this.requestedAmount = 0
      this.feeList = []
      this.fetchRefundableAmount()
    },
    async fetchRefundableAmount() {
      const deviceOrderId = this.selectedDeviceId
      if (!deviceOrderId) return
      this.amountLoading = true
      try {
        const res = await http({
          url: api.platformRefundRefundableInfo,
          type: 'post',
          data: {
            supplierReturnOrderId: this.supplierReturnOrderId,
            deviceOrderId,
          },
        })
        // 防止切换设备时旧请求覆盖新结果
        if (this.selectedDeviceId !== deviceOrderId) return
        const result = (res && res.result) || {}
        this.requestedAmount = result.requestedAmount || 0
        this.feeList = result.feeList || []
      } finally {
        this.amountLoading = false
      }
    },
    async onSubmit() {
      if (!this.selectedDeviceId) {
        this.$warning({
          title: this.$t('layer.Warning'),
          content: this.$t('选择退款设备'),
        })
        return
      }
      if (Number(this.requestedAmount) <= 0) {
        this.$warning({
          title: this.$t('layer.Warning'),
          content: this.$t('当前无可退款金额'),
        })
        return
      }
      this.submitting = true
      try {
        const res = await http({
          url: api.platformRefundCreate,
          type: 'post',
          hasLoading: true,
          data: {
            supplierReturnOrderId: this.supplierReturnOrderId,
            deviceOrderId: this.selectedDeviceId,
          },
        })
        const result = (res && res.result) || {}
        const pendingStatus = ['SUBMITTING', 'UNKNOWN', 'WAIT_PLATFORM_RESULT']
        this.$success({
          title: this.$t('layer.Success'),
          content: pendingStatus.includes(result.status)
            ? this.$t('提交成功，结果待确认')
            : this.$t('提交成功'),
        })
        this.applyVisible = false
        this.pageNo = 1
        this.fetchList()
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.platform-refund-panel {
  .pr-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .pr-title {
    font-size: 16px;
    font-weight: 900;
    color: rgba(0, 0, 0, 0.85);
    span {
      margin-left: 5px;
    }
  }
  .pr-actions {
    display: flex;
    align-items: center;
  }
  .pr-footer {
    margin-top: 10px;
    text-align: right;
  }
  .pr-section-title {
    margin: 10px 0;
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }
  .pr-amount {
    color: #ff4d4f;
    font-weight: 700;
  }
  .pr-device-list {
    display: flex;
    flex-direction: column;
  }
  .pr-device-item {
    margin-bottom: 8px;
    white-space: normal;
  }
  .pr-empty {
    padding: 16px 0;
    color: rgba(0, 0, 0, 0.45);
  }
  .pr-modal-footer {
    margin-top: 16px;
    text-align: right;
  }
}
</style>
